import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
  ClassicEditor,
  Essentials,
  Paragraph,
  Bold,
  Italic,
  Underline,
  Link,
  List,
  Alignment,
  Font,
  Heading,
  Table,
  TableToolbar,
  Image,
  ImageToolbar,
  ImageResize,
  ImageUpload,
  FileRepository
} from 'ckeditor5';

import 'ckeditor5/ckeditor5.css';
import { getCommunicationTemplates, sendCommunication } from '../../api/communicationTemplates';
import { userList } from '../../api/user';

// Custom upload adapter for CKEditor - converts images to base64 data URLs
class MyUploadAdapter {
  constructor(loader) {
    this.loader = loader;
  }

  async upload() {
    const file = await this.loader.file;
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        resolve({ default: reader.result });
      };
      reader.onerror = error => {
        reject(error);
      };
      reader.readAsDataURL(file);
    });
  }

  abort() {
    // Abort upload if needed
  }
}

function MyCustomUploadAdapterPlugin(editor) {
  editor.plugins.get('FileRepository').createUploadAdapter = (loader) => {
    return new MyUploadAdapter(loader);
  };
}

const SendEmail = () => {
  // Company selection logic
  let companyOptions = [];
  const companiesStr = localStorage.getItem('companies');
  if (companiesStr) {
    try {
      const companies = JSON.parse(companiesStr);
      if (Array.isArray(companies) && companies.length > 0) {
        companyOptions = [
          ...companies.map(c => ({
            value: c.id,
            label: c.company_name
          }))
        ];
      }
    } catch (e) {
      companyOptions = [];
    }
  }
  const [selectedCompany, setSelectedCompany] = useState('');

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailContent, setEmailContent] = useState('');
  
  const [recipientType, setRecipientType] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [sending, setSending] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [pdfAttachments, setPdfAttachments] = useState([]);
  const [pdfError, setPdfError] = useState('');
  
  // Test email functionality
  const [showTestEmailModal, setShowTestEmailModal] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  
  // Preview functionality
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  const recipientOptions = [
    { value: 'individual', label: 'Individual Users' },
    { value: 'active_users', label: 'Active Users' },
    { value: 'trial_users', label: 'Trial Users' },
    { value: 'inactive_users', label: 'Inactive Users' },
    { value: 'users_with_bookings', label: 'Users with Bookings' },
    { value: 'users_no_bookings', label: 'Users with No Current Bookings' },
  ];

  const userOptions = users.map(user => ({
    value: user.id,
    label: `${user.first_name || ''} ${user.last_name || ''} (${user.email})`.trim()
  }));

  const selectedUserOptions = userOptions.filter(option => selectedUsers.includes(option.value));

  useEffect(() => {
    // Only fetch if either no company options (not super admin) or company is selected
    if (companyOptions.length === 0 || selectedCompany) {
      fetchEmailTemplates();
      fetchUsers();
    }
  }, [selectedCompany]);

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const payload = { page: 1, limit: 10000 };
      if (selectedCompany) {
        payload.company_id = selectedCompany.value;
      }
      const response = await userList(payload);
      if (response.code === 1 && response.data) {
        setUsers(response.data.user_list || response.data || []);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchEmailTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = { template_type: 'email' };
      // Always pass company_id - either from selectedCompany or empty string
      payload.company_id = selectedCompany ? selectedCompany.value : '';
      const response = await getCommunicationTemplates(payload);
      if (response.code) {
        // Flatten the hierarchical structure to get all templates
        const flattenTemplates = (items) => {
          let result = [];
          items.forEach(item => {
            if (item.type === 'template') {
              result.push({
                id: item.id,
                name: item.name,
                subject: item.subject,
                content: item.content,
                pdf_attachments: item.pdf_attachments || []
              });
            }
            if (item.subfolders && item.subfolders.length > 0) {
              result = result.concat(flattenTemplates(item.subfolders));
            }
          });
          return result;
        };
        const allTemplates = flattenTemplates(response.data);
        setTemplates(allTemplates);
      } else {
        setError(response.message || 'Failed to fetch templates');
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError('Error fetching templates');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateChange = (e) => {
    const templateId = parseInt(e.target.value);
    const template = templates.find(t => t.id === templateId);
    
    if (template) {
      setSelectedTemplate(template);
      setEmailSubject(template.subject || '');
      setEmailContent(template.content || '');
      // Ensure pdf_attachments is always an array
      const attachments = template.pdf_attachments || [];
      
      // Parse if it's a string, otherwise use as-is
      let parsedAttachments = attachments;
      if (typeof attachments === 'string') {
        try {
          parsedAttachments = JSON.parse(attachments);
        } catch (e) {
          console.error('Error parsing pdf_attachments:', e);
          parsedAttachments = [];
        }
      }
      
      setPdfAttachments(Array.isArray(parsedAttachments) ? parsedAttachments : []);
    }
  };

  const MAX_PDF_SIZE = 10 * 1024 * 1024; // 10MB

  const isPdfFile = (file) =>
    file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

  const handlePdfUpload = (e) => {
    const files = Array.from(e.target.files);
    const invalidFiles = files.filter(file => !isPdfFile(file));
    if (invalidFiles.length > 0) {
      setPdfError('Only PDF attachments are accepted.');
      e.target.value = '';
      return;
    }
    const oversizedFile = files.find(file => file.size > MAX_PDF_SIZE);
    if (oversizedFile) {
      setPdfError(`File "${oversizedFile.name}" exceeds the 10MB size limit (${(oversizedFile.size / (1024 * 1024)).toFixed(2)}MB)`);
      e.target.value = '';
      return;
    }
    setPdfError('');
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        setPdfAttachments(prev => [...prev, {
          filename: file.name,
          content: base64,
          type: 'application/pdf'
        }]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const removePdfAttachment = (index) => {
    setPdfAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleUserSelection = (selectedOptions) => {
    const selectedIds = selectedOptions ? selectedOptions.map(option => option.value) : [];
    setSelectedUsers(selectedIds);
  };

  const handleSendEmail = async () => {
    // Validation
    if (!selectedTemplate) {
      setError('Please select an email template');
      return;
    }
    if (!recipientType) {
      setError('Please select recipient type');
      return;
    }
    if (recipientType === 'individual' && selectedUsers.length === 0) {
      setError('Please select at least one user');
      return;
    }

    setSending(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await sendCommunication({
        template_type: 'email',
        subject: emailSubject,
        content: emailContent,
        recipient_type: recipientType,
        recipient_ids: selectedUsers,
        pdf_attachments: pdfAttachments
      });

      if (response.code === 1) {
        setSuccess(`Email sent successfully to ${response.data.total_users} users. Success: ${response.data.success_count}, Failed: ${response.data.failure_count}`);
        // Reset form
        setSelectedTemplate(null);
        setEmailSubject('');
        setEmailContent('');
        setRecipientType('');
        setSelectedUsers([]);
        setPdfAttachments([]);
      } else {
        setError(response.message || 'Failed to send email');
      }
    } catch (err) {
      console.error('Error sending email:', err);
      setError('Error sending email');
    } finally {
      setSending(false);
    }
  };

  const handleSendTestEmail = async () => {
    // Validation
    if (!selectedTemplate) {
      setError('Please select an email template');
      return;
    }
    if (!testEmailAddress) {
      setError('Please enter a test email address');
      return;
    }

    setSendingTestEmail(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await sendCommunication({
        template_type: 'email',
        subject: emailSubject,
        content: emailContent,
        test_email: testEmailAddress,
        is_test: true,
        pdf_attachments: pdfAttachments
      });

      if (response.code === 1) {
        setSuccess(`Test email sent successfully to ${testEmailAddress}`);
        setShowTestEmailModal(false);
        setTestEmailAddress('');
      } else {
        setError(response.message || 'Failed to send test email');
      }
    } catch (err) {
      console.error('Error sending test email:', err);
      setError('Error sending test email');
    } finally {
      setSendingTestEmail(false);
    }
  };

  return (
    <article className="col-lg-9 articleClass">
      <div className="article-body store-bd-gy">
        <div className="row align-items-center mb-4">
          <div className="col-sm-12">
            <h3 className="page-title">Send Email</h3>
            <p className="text-muted">Send email to users using email templates</p>
          </div>
        </div>

        {/* Company Selection - Only show if there are company options */}
        {companyOptions.length > 0 && (
          <div className="row mb-4">
            <div className="col-md-6">
              <label className="form-label">Select Company</label>
              <Select
                options={companyOptions}
                value={selectedCompany}
                onChange={(selected) => {
                  setSelectedCompany(selected);
                }}
                placeholder="Select Company"
              />
            </div>
          </div>
        )}

        {/* Show company selection message if no company is selected */}
        {companyOptions.length > 0 && !selectedCompany && (
          <div className="alert alert-info mb-4">
            Please select a company to send emails.
          </div>
        )}

        <div className="card">
          <div className="card-body">
            {error && (
              <div className="alert alert-danger mb-3">
                {error}
                <button type="button" className="btn-close float-end" onClick={() => setError(null)}></button>
              </div>
            )}
            {success && (
              <div className="alert alert-success mb-3">
                {success}
                <button type="button" className="btn-close float-end" onClick={() => setSuccess(null)}></button>
              </div>
            )}

            {/* Show content only if no company options (not super admin) or company is selected */}
            {companyOptions.length > 0 && !selectedCompany ? (
              null
            ) : (
            <>

            <div className="row">
              {/* Template Selection */}
              <div className="col-md-6 mb-4">
                <label className="form-label fw-bold">
                  Select Email Template <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  value={selectedTemplate?.id || ''}
                  onChange={handleTemplateChange}
                >
                  <option value="">Select Template</option>
                  {templates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Recipient Type Selection */}
              <div className="col-md-6 mb-4">
                <label className="form-label fw-bold">
                  Recipient Type <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  value={recipientType}
                  onChange={(e) => setRecipientType(e.target.value)}
                >
                  <option value="">Select Recipient Type</option>
                  {recipientOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Individual User Selection */}
              {recipientType === 'individual' && (
                <div className="col-md-12 mb-4">
                  <label className="form-label fw-bold">
                    Select Users <span className="text-danger">*</span>
                  </label>
                  <Select
                    isMulti
                    options={userOptions}
                    value={selectedUserOptions}
                    onChange={handleUserSelection}
                    placeholder="Search and select users..."
                    isSearchable
                    isClearable
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                </div>
              )}

              {/* Email Subject */}
              <div className="col-md-12 mb-4">
                <label className="form-label fw-bold">
                  Email Subject <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="Enter email subject"
                />
              </div>

              {/* Email Content */}
              <div className="col-md-12 mb-4">
                <label className="form-label fw-bold">
                  Email Content <span className="text-danger">*</span>
                </label>
                <div style={{ minHeight: '300px' }}>
                  <CKEditor
                    editor={ClassicEditor}
                    config={{
                      licenseKey: 'GPL',
                      plugins: [
                        Essentials,
                        Paragraph,
                        Bold,
                        Italic,
                        Underline,
                        Link,
                        List,
                        Alignment,
                        Font,
                        Heading,
                        Table,
                        TableToolbar,
                        Image,
                        ImageToolbar,
                        ImageResize,
                        ImageUpload,
                        MyCustomUploadAdapterPlugin
                      ],
                      toolbar: [
                        'undo', 'redo', '|',
                        'heading', '|',
                        'bold', 'italic', 'underline', '|',
                        'fontSize', 'fontFamily', 'fontColor', 'fontBackgroundColor', '|',
                        'alignment:left', 'alignment:center', 'alignment:right', 'alignment:justify', '|',
                        'link', 'uploadImage', 'insertTable', '|',
                        'bulletedList', 'numberedList', 'outdent', 'indent', '|',
                        'removeFormat'
                      ],
                      heading: {
                        options: [
                          { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
                          { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
                          { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
                          { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
                        ]
                      },
                      fontSize: {
                        options: [
                          10, 12, 14, 'default', 18, 20, 24, 28, 32, 36, 48
                        ]
                      },
                      fontFamily: {
                        options: [
                          'default',
                          'Arial, Helvetica, sans-serif',
                          'Courier New, Courier, monospace',
                          'Georgia, serif',
                          'Lucida Sans Unicode, Lucida Grande, sans-serif',
                          'Tahoma, Geneva, sans-serif',
                          'Times New Roman, Times, serif',
                          'Trebuchet MS, Helvetica, sans-serif',
                          'Verdana, Geneva, sans-serif'
                        ]
                      },
                      image: {
                        toolbar: [
                          'imageTextAlternative', '|',
                          'imageStyle:inline', 'imageStyle:wrapText', 'imageStyle:breakText', 'imageStyle:side', '|',
                          'resizeImage', '|',
                          'toggleImageCaption', '|',
                          'imageRemove'
                        ],
                        styles: [
                          'inline',
                          'alignLeft',
                          'alignCenter',
                          'alignRight',
                          'wrapText',
                          'breakText',
                          'side'
                        ],
                        resizeUnit: 'px'
                      },
                      table: {
                        contentToolbar: [
                          'tableColumn', 'tableRow', 'mergeTableCells'
                        ]
                      }
                    }}
                    data={emailContent}
                    onReady={editor => {
                      editor.editing.view.change(writer => {
                        writer.setStyle('min-height', '300px', editor.editing.view.document.getRoot());
                      });
                    }}
                    onChange={(event, editor) => {
                      const data = editor.getData();
                      setEmailContent(data);
                    }}
                  />
                </div>
              </div>

              {/* PDF Attachments */}
              <div className="col-md-12 mb-4">
                <label className="form-label fw-bold">
                  PDF Attachments
                </label>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handlePdfUpload}
                  className="form-control"
                  multiple
                />
                <small className="text-muted">Only PDF attachments are accepted. Multiple PDF files are allowed.</small>
                {pdfError && (
                  <div className="text-danger small mt-1">
                    {pdfError}
                  </div>
                )}
                {pdfAttachments.length > 0 && (
                  <div className="mt-2">
                    <strong>Attached PDFs:</strong>
                    <ul className="list-group mt-1">
                      {pdfAttachments.map((pdf, index) => (
                        <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                          <div className="d-flex align-items-center gap-2">
                            <a
                              href={`data:application/pdf;base64,${pdf.content}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-decoration-none"
                              onClick={() => console.log('PDF content length:', pdf.content?.length, 'Filename:', pdf.filename)}
                            >
                              <i className="fa fa-file-pdf me-2" style={{ color: '#dc3545' }}></i>
                              {pdf.filename}
                            </a>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => {
                                const link = document.createElement('a');
                                link.href = `data:application/pdf;base64,${pdf.content}`;
                                link.download = pdf.filename;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              }}
                            >
                              <i className="fa fa-download"></i>
                            </button>
                          </div>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => removePdfAttachment(index)}
                          >
                            <i className="fa fa-trash"></i>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Send Button */}
              <div className="col-md-12 d-flex gap-2">
                <button
                  className="btn btn-primary"
                  onClick={handleSendEmail}
                  disabled={sending}
                >
                  {sending ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Sending...
                    </>
                  ) : (
                    'Send Email'
                  )}
                </button>
                <button
                  className="btn btn-info"
                  onClick={() => setShowTestEmailModal(true)}
                  disabled={sending}
                >
                  Send Test Mail
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowPreviewModal(true)}
                  disabled={sending}
                >
                  Preview
                </button>
              </div>
            </div>
            </>
            )}
          </div>
        </div>
      </div>

      {/* Test Email Modal */}
      {showTestEmailModal && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Send Test Email</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowTestEmailModal(false);
                    setTestEmailAddress('');
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label fw-bold">
                    Test Email Address <span className="text-danger">*</span>
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    value={testEmailAddress}
                    onChange={(e) => setTestEmailAddress(e.target.value)}
                    placeholder="Enter test email address"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowTestEmailModal(false);
                    setTestEmailAddress('');
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSendTestEmail}
                  disabled={sendingTestEmail}
                >
                  {sendingTestEmail ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Sending...
                    </>
                  ) : (
                    'Send Test Email'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex' }}>
          <div className="modal-dialog modal-dialog-centered modal-xl">
            <div className="modal-content" style={{ border: 'none', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
              <div className="modal-header" style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}>
                <h5 className="modal-title mb-0" style={{ color: '#333', fontWeight: '600' }}>
                  Email Preview
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowPreviewModal(false)}
                  style={{ fontSize: '1.2rem' }}
                ></button>
              </div>
              <div className="modal-body p-0" style={{ backgroundColor: '#ffffff' }}>
                <div style={{ 
                  backgroundColor: '#ffffff', 
                  padding: '20px 24px',
                  borderBottom: '1px solid #e9ecef'
                }}>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ 
                      fontSize: '0.75rem', 
                      color: '#6c757d', 
                      textTransform: 'uppercase', 
                      letterSpacing: '0.5px',
                      fontWeight: '500',
                      marginRight: '8px'
                    }}>
                      Subject:
                    </label>
                    <span style={{ 
                      fontSize: '1.1rem', 
                      color: '#212529', 
                      fontWeight: '500'
                    }}>
                      {emailSubject || 'No subject'}
                    </span>
                  </div>
                </div>
                <div style={{ 
                  padding: '0',
                  backgroundColor: '#ffffff'
                }}>
                  <iframe
                    title="Email Preview"
                    style={{ 
                      width: '100%',
                      height: '600px',
                      border: 'none',
                      margin: '20px'
                    }}
                    sandbox="allow-same-origin"
                    srcDoc={`
                      <!DOCTYPE html>
                      <html>
                      <head>
                        <style>
                          * {
                            margin: 0;
                            padding: 0;
                            box-sizing: border-box;
                          }
                          body {
                            padding: 0;
                            margin: 0;
                            line-height: 1.6;
                          }
                          img {
                            max-width: 100%;
                            height: auto;
                          }
                          p, div, span, td, th {
                            line-height: inherit;
                          }
                        </style>
                      </head>
                      <body>
                        ${emailContent || '<div style="color: #6c757d; text-align: center; padding: 40px;">No content</div>'}
                      </body>
                      </html>
                    `}
                  />
                </div>
              </div>
              <div className="modal-footer" style={{ 
                backgroundColor: '#f8f9fa', 
                borderTop: '1px solid #e9ecef',
                padding: '16px 24px'
              }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowPreviewModal(false)}
                  style={{ 
                    padding: '8px 24px',
                    fontWeight: '500'
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </article>
  );
};

export default SendEmail;
